import type { ListingVariant } from '@ps/models/lib/listing';
import type { PostContentType } from '@ps/models/lib/post';

import type { ChatSessionType } from './ChatSessionType';
import type { ChatUser } from './ChatUser';

export enum MsgSendStatus {
  sending = 1,
  success = 2,
  fail = 3,
}

export enum ChatMessageType {
  SystemMessage = 0,
  Text = 1,
  Picture = 2,
  Product = 3,
  Order = 4,
  Video = 5,
  Live = 6,
  Post = 7,
}

export type ChatMessageBase = {
  messageId: number | string;
  clientId: string;
  from: ChatUser;
  to?: ChatUser;
  sessionId?: string;
  sessionType: ChatSessionType;
  chatSessionId?: number | string;
  roomId: string;
  time: number;
  type: ChatMessageType;
  customType?: string;
  text?: string;
  quoteMsg?: ChatMessage;
  isAiMessage?: boolean;
};

export type ChatTextMessage = ChatMessageBase & {
  type: ChatMessageType.Text;
  content: TextContent;
};

export type ChatSystemMessage = ChatMessageBase & {
  type: ChatMessageType.SystemMessage;
  content: SystemMessageContent;
};

export type ChatPictureMessage = ChatMessageBase & {
  type: ChatMessageType.Picture;
  content: ImageContent;
};

export type ChatVideoMessage = ChatMessageBase & {
  type: ChatMessageType.Video;
  content: VideoContent;
};

export type ChatOrderMessage = ChatMessageBase & {
  type: ChatMessageType.Order;
  content: OrderContent;
};

export type ChatProductMessage = ChatMessageBase & {
  type: ChatMessageType.Product;
  content: ProductContent;
};

export type ChatLiveMessage = ChatMessageBase & {
  type: ChatMessageType.Live;
  content: LiveMessageContent;
};

export type ChatPostMessage = ChatMessageBase & {
  type: ChatMessageType.Post;
  content: PostMessageContent;
};

export type ChatMessage =
  | ChatTextMessage
  | ChatPictureMessage
  | ChatVideoMessage
  | ChatOrderMessage
  | ChatProductMessage
  | ChatSystemMessage
  | ChatLiveMessage
  | ChatPostMessage;

export type ClientChatMessage = ChatMessage & {
  lastSendTime: number;
  sendStatus: MsgSendStatus;
  lastSendErrorMsg?: string;
};

export interface TextContent {
  text: string;
}

/**
 * Local media variant carries the picked `File` transiently so the send
 * pipeline can upload it (see `prepareMessageContent`). It is stripped before
 * the content is `JSON.stringify`'d for the wire and is ignored by renderers
 * (which read `localFileUri`); a `File` isn't JSON-serializable, so it does not
 * survive a `localStorage` round-trip — an in-session retry re-uses the held
 * `File`, a post-reload retry can't (matches upstream).
 */
export type LocalMedia = {
  localFileUri: string;
  localFile?: File;
};

export type ImageContent = {
  width: number;
  height: number;
} & (
  | LocalMedia
  | {
      imageUrl: string;
      thumbnailUrl?: string;
    }
);

export type VideoThumbnail = {
  width: number;
  height: number;
} & ({ localFileUri: string } | { url: string });

export type VideoContent = {
  width?: number;
  height?: number;
  thumbnail?: VideoThumbnail;
} & (LocalMedia | { videoUrl: string });

export type OrderContent = {
  orderId: string;
  orderNumber: string;
  imageUrl: string;
  orderTitle: string;
  price: number;
  quantity: number;
  sellerId: string;
  sellerName: string;
  buyerId: string;
  buyerName: string;
  orderDate: string;
  from: 'buyer' | 'seller';
};

export type ProductContent = {
  id: string;
  imageUrl: string;
  name: string;
  quantity: number;
  price: number;
  discountPrice?: number;
  variants: ListingVariant[] | undefined;
  storeId: string;
  storeName: string;
};

export type LiveMessageContent = {
  liveStreamId: string;
};

export type PostMessageContent = {
  storeId: string;
  postId: string;
  postType?: PostContentType;
  message: string;
  permalink: string;
  title: string;
  description: string;
  createdAt: string;
  publishedAt: string;
  imageCount?: number;
  videoCount?: number;
};

export type CustomContent = {
  data: unknown;
};

export enum SystemMessageType {
  Text = 1,
  OrderAnnouncement = 2,
}

export type SystemMessageContent =
  | {
      type: SystemMessageType.Text;
      content: TextContent;
    }
  | {
      type: SystemMessageType.OrderAnnouncement;
      content: OrderContent;
    };
